import { useEffect, useState, Fragment } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconButton, Checkbox, FormControlLabel, Snackbar } from '@mui/material';
import CustomButton from '../reusable/CustomButton';
import CustomTooltip from '../reusable/CustomTooltip';
import Loader from '../reusable/Loader';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import NewWindowIcon from '../assets/footer-new-window.svg';
import InfoIcon from '../assets/info-icon.svg';
import WarningIcon from '../assets/alert-warning.svg';
import StudentVoiceStatus from './StudentVoiceStatus';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import {
  getSchoolReportQuotes,
  GetSchoolReportQuotesResponse,
  patchSchoolReportQuotes,
  JsonPatchOperation,
} from '../../services/studentVoiceService';
import {
  executeQuoteSelectionWorkflowTransition,
  getQuoteSelectionWorkflowStatus,
} from '../../services/workflowService';
import { getConfiguredSchools } from '../../services/eoaConfigServices';
import TranscriptReviewModal from '../transcripts/TranscriptReviewModal';
import { AxiosError } from 'axios';
import { USER_ROLES } from '../../constants';
import './StudentVoiceReview.css';

const QUOTE_FIELDS: QuoteTextField[] = [
  'key_findings_ending_student_voice',
  'college_eligibility_student_voices_1_1',
  'college_eligibility_student_voices_1_2',
  'college_eligibility_student_voices_2_1',
  'college_eligibility_student_voices_2_2',
  'college_readiness_student_voices_1_1',
  'college_readiness_student_voices_1_2',
  'college_readiness_student_voices_2_1',
  'college_readiness_student_voices_2_2',
  'pss_via_cte_student_voices_1',
  'pss_via_cte_student_voices_2',
  'next_steps_student_voices',
];

type SkipField =
  | 'key_findings_ending_student_voice_is_skip'
  | 'college_eligibility_student_voices_1_is_skip'
  | 'college_eligibility_student_voices_2_is_skip'
  | 'college_readiness_student_voices_1_is_skip'
  | 'college_readiness_student_voices_2_is_skip'
  | 'pss_via_cte_student_voices_is_skip'
  | 'next_steps_student_voices_is_skip';

type QuoteTextField = Exclude<keyof GetSchoolReportQuotesResponse, SkipField | 'eoa_school_id'>;

const createEmptyQuotes = (schoolId: string): GetSchoolReportQuotesResponse => ({
  eoa_school_id: schoolId,
  student_voices_focus_group_summary_1: null,
  student_voices_focus_group_summary_2: null,
  student_voices_focus_group_summary_3: null,
  student_voices_focus_group_summary_4: null,
  key_findings_ending_student_voice: null,
  college_eligibility_student_voices_1_1: null,
  college_eligibility_student_voices_1_2: null,
  college_eligibility_student_voices_2_1: null,
  college_eligibility_student_voices_2_2: null,
  college_readiness_student_voices_1_1: null,
  college_readiness_student_voices_1_2: null,
  college_readiness_student_voices_2_1: null,
  college_readiness_student_voices_2_2: null,
  pss_via_cte_student_voices_1: null,
  pss_via_cte_student_voices_2: null,
  next_steps_student_voices: null,
  key_findings_ending_student_voice_is_skip: false,
  college_eligibility_student_voices_1_is_skip: false,
  college_eligibility_student_voices_2_is_skip: false,
  college_readiness_student_voices_1_is_skip: false,
  college_readiness_student_voices_2_is_skip: false,
  pss_via_cte_student_voices_is_skip: false,
  next_steps_student_voices_is_skip: false,
});

const StudentVoiceReview = () => {
  const navigate = useNavigate();
  const { schoolId } = useParams<{ schoolId: string }>();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState<GetSchoolReportQuotesResponse | null>(null);
  const [editedQuotes, setEditedQuotes] = useState<GetSchoolReportQuotesResponse | null>(null);
  const [schoolName, setSchoolName] = useState<string>('');
  const [workflowStatus, setWorkflowStatus] = useState<string | null>(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isLockModalOpen, setIsLockModalOpen] = useState(false);
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAutoSaveMessage, setShowAutoSaveMessage] = useState(false);

  const isEditable =
    workflowStatus === 'QUOTE_SELECTION_IN_PROGRESS' || workflowStatus === 'QUOTE_SELECTION_NOT_STARTED';
  const userRole = userDetails?.role;
  const isAdmin = userRole === USER_ROLES.XQ_ADMIN;
  const isLeadFacilitator = userRole === USER_ROLES.LEAD_FACILITATOR;
  const showReopenButton = isAdmin && workflowStatus === 'QUOTE_SELECTION_FINALIZED';
  const canApproveOrReject = (isAdmin || isLeadFacilitator) && workflowStatus === 'QUOTE_SELECTION_COMPLETED';

  useEffect(() => {
    const fetchData = async () => {
      if (!userDetails || !schoolId) return;

      try {
        setLoading(true);

        const [quotesResponse, schoolsResponse, statusResponse] = await Promise.all([
          getSchoolReportQuotes(userDetails.eoaId, schoolId, userDetails.access_token).catch(() => null),
          getConfiguredSchools(userDetails.eoaId, userDetails.access_token),
          getQuoteSelectionWorkflowStatus(userDetails.eoaId, schoolId, userDetails.access_token),
        ]);

        if (quotesResponse) {
          setQuotes(quotesResponse);
          const wrappedQuotes = { ...quotesResponse };
          QUOTE_FIELDS.forEach((field) => {
            if (wrappedQuotes[field]) {
              wrappedQuotes[field] = wrapQuoteInQuotes(wrappedQuotes[field]) as any;
            }
          });
          setEditedQuotes(wrappedQuotes);
        } else {
          const emptyQuotes = createEmptyQuotes(schoolId);
          setQuotes(emptyQuotes);
          setEditedQuotes({ ...emptyQuotes });
        }

        const school = schoolsResponse.data.find((s: any) => s.id === schoolId);
        if (school) {
          setSchoolName(school.name);
        }

        if (statusResponse) {
          setWorkflowStatus(statusResponse.current_state);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        handleOpenErrorModal('Error occurred while fetching student voice data!', error as AxiosError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userDetails, schoolId, handleOpenErrorModal]);

  const handleWorkflowTransition = async (
    transitionName: string,
    closeModal: () => void,
    errorMessage: string,
    metadata?: Record<string, any>
  ) => {
    if (!userDetails || !schoolId) return;

    setIsProcessing(true);
    try {
      await executeQuoteSelectionWorkflowTransition(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token,
        transitionName,
        metadata
      );
      const statusResponse = await getQuoteSelectionWorkflowStatus(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token
      );
      setWorkflowStatus(statusResponse.current_state);
      closeModal();
    } catch (error) {
      console.error(`Error ${transitionName}:`, error);
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        console.error('Error response:', axiosError.response.data);
        console.error('Error status:', axiosError.response.status);
      }
      handleOpenErrorModal(errorMessage, axiosError);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprove = () =>
    handleWorkflowTransition(
      'approve_quote_selection',
      () => setIsApproveModalOpen(false),
      'Error occurred while approving quote selection!'
    );

  const refreshQuotes = async () => {
    if (!userDetails || !schoolId) return;
    try {
      const quotesResponse = await getSchoolReportQuotes(userDetails.eoaId, schoolId, userDetails.access_token);
      setQuotes(quotesResponse);
      const wrappedQuotes = { ...quotesResponse };
      QUOTE_FIELDS.forEach((field) => {
        if (wrappedQuotes[field]) {
          wrappedQuotes[field] = wrapQuoteInQuotes(wrappedQuotes[field]) as any;
        }
      });
      setEditedQuotes(wrappedQuotes);
    } catch (error) {}
  };

  const handleReject = () => {
    if (!userDetails) return;

    const metadata = {
      rejected_at: new Date().toISOString(),
      rejected_reason: 'Rejected by lead facilitator for review and editing',
      rejected_by: userDetails.name || 'Unknown',
      rejected_by_role: userDetails.role || 'Unknown',
    };

    handleWorkflowTransition(
      'reject_quote_selection',
      () => {
        setIsRejectModalOpen(false);
        refreshQuotes();
      },
      'Error occurred while rejecting quote selection!',
      metadata
    );
  };

  const handleReopen = () => {
    handleWorkflowTransition(
      'reopen_quote_selection',
      () => {
        setIsReopenModalOpen(false);
        refreshQuotes();
      },
      'Error occurred while reopening quote selection!'
    );
  };

  const handleQuoteChange = (field: QuoteTextField, value: string) => {
    if (!editedQuotes) return;
    setEditedQuotes({ ...editedQuotes, [field]: value });
  };

  const wrapQuoteInQuotes = (value: string | null | undefined): string => {
    if (!value || !value.trim()) return '';

    const trimmed = value.trim();
    if (trimmed === '“' || trimmed === '”' || trimmed === '“”') return '';

    let text = trimmed;
    text = text.replace(/^[“”]+/, '').replace(/[“”]+$/, '');

    if (!text.startsWith('“')) {
      text = `“${text}`;
    }

    if (text.length > 1 && !text.endsWith('”')) {
      text = `${text}”`;
    }

    return text;
  };

  const handleQuoteWithQuotes = (
    field: QuoteTextField,
    value: string,
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    if (!editedQuotes) return;

    const textWithoutQuotes = value.replace(/[“”]/g, '');
    if (!textWithoutQuotes || textWithoutQuotes.trim() === '') {
      setEditedQuotes({ ...editedQuotes, [field]: '“”' });
      setTimeout(() => {
        event.target.setSelectionRange(1, 1);
      }, 0);
      return;
    }

    setEditedQuotes({ ...editedQuotes, [field]: `“${textWithoutQuotes}”` });

    const cursorPosition = textWithoutQuotes.length + 1;
    setTimeout(() => {
      event.target.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  const handleQuotePaste = (field: QuoteTextField, event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const textWithoutQuotes = pastedText.replace(/[“”]/g, '');

    if (!editedQuotes) return;

    const currentValue = editedQuotes[field] || '';
    const currentTextWithoutQuotes = currentValue.replace(/[“”]/g, '');
    const selectionStart = (event.target as HTMLTextAreaElement).selectionStart || 0;
    const selectionEnd = (event.target as HTMLTextAreaElement).selectionEnd || 0;

    const newText =
      currentTextWithoutQuotes.substring(0, selectionStart) +
      textWithoutQuotes +
      currentTextWithoutQuotes.substring(selectionEnd);

    if (!newText || newText.trim() === '') {
      setEditedQuotes({ ...editedQuotes, [field]: '“”' });
    } else {
      setEditedQuotes({ ...editedQuotes, [field]: `“${newText}”` });
    }

    setTimeout(() => {
      const newCursorPosition = Math.min(selectionStart + textWithoutQuotes.length, newText.length + 1);
      (event.target as HTMLTextAreaElement).setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleSummaryPaste = (field: QuoteTextField, event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');

    if (!editedQuotes) return;

    const currentValue = editedQuotes[field] || '';
    const selectionStart = (event.target as HTMLTextAreaElement).selectionStart || 0;
    const selectionEnd = (event.target as HTMLTextAreaElement).selectionEnd || 0;

    const newText = currentValue.substring(0, selectionStart) + pastedText + currentValue.substring(selectionEnd);

    setEditedQuotes({ ...editedQuotes, [field]: newText });

    setTimeout(() => {
      const newCursorPosition = selectionStart + pastedText.length;
      (event.target as HTMLTextAreaElement).setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleQuoteDrop = (field: QuoteTextField, event: React.DragEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    const droppedText = event.dataTransfer.getData('text');
    const textWithoutQuotes = droppedText.replace(/[“”]/g, '');

    if (!editedQuotes) return;

    const currentValue = editedQuotes[field] || '';
    const currentTextWithoutQuotes = currentValue.replace(/[“”]/g, '');
    const selectionStart = (event.target as HTMLTextAreaElement).selectionStart || 0;
    const selectionEnd = (event.target as HTMLTextAreaElement).selectionEnd || 0;

    const newText =
      currentTextWithoutQuotes.substring(0, selectionStart) +
      textWithoutQuotes +
      currentTextWithoutQuotes.substring(selectionEnd);

    if (!newText || newText.trim() === '') {
      setEditedQuotes({ ...editedQuotes, [field]: '“”' });
    } else {
      setEditedQuotes({ ...editedQuotes, [field]: `“${newText}”` });
    }

    setTimeout(() => {
      const newCursorPosition = Math.min(selectionStart + textWithoutQuotes.length, newText.length + 1);
      (event.target as HTMLTextAreaElement).setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleSummaryDrop = (field: QuoteTextField, event: React.DragEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    const droppedText = event.dataTransfer.getData('text');

    if (!editedQuotes) return;

    const currentValue = editedQuotes[field] || '';
    const selectionStart = (event.target as HTMLTextAreaElement).selectionStart || 0;
    const selectionEnd = (event.target as HTMLTextAreaElement).selectionEnd || 0;

    const newText = currentValue.substring(0, selectionStart) + droppedText + currentValue.substring(selectionEnd);

    setEditedQuotes({ ...editedQuotes, [field]: newText });

    setTimeout(() => {
      const newCursorPosition = selectionStart + droppedText.length;
      (event.target as HTMLTextAreaElement).setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const getQuoteDisplayValue = (field: QuoteTextField): string => {
    const value = editedQuotes?.[field];
    if (!value || value === '“”') return '';
    return value;
  };

  const handleQuoteFocus = (field: QuoteTextField, event: React.FocusEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (!value || value === '“”') {
      if (!editedQuotes) return;
      setEditedQuotes({ ...editedQuotes, [field]: '“”' });
      setTimeout(() => {
        event.target.setSelectionRange(1, 1);
      }, 0);
    } else if (value === '“”') {
      setTimeout(() => {
        event.target.setSelectionRange(1, 1);
      }, 0);
    }
  };

  const isSectionSkipped = (skipField: SkipField): boolean => {
    return editedQuotes?.[skipField] === true;
  };

  const isSavedSectionSkipped = (skipField: SkipField): boolean => {
    return quotes?.[skipField] === true;
  };

  const handleSkipSection = async (skipField: SkipField, checked: boolean) => {
    if (!editedQuotes || !userDetails || !schoolId) return;
    setEditedQuotes((prev) => (prev ? { ...prev, [skipField]: checked } : prev));
    try {
      await patchSchoolReportQuotes(userDetails.eoaId, schoolId, userDetails.access_token, [
        { op: 'replace', path: `/${skipField}`, value: checked },
      ]);
      setQuotes((prev) => (prev ? { ...prev, [skipField]: checked } : prev));
      setShowAutoSaveMessage(true);
    } catch (error) {
      console.error('Error saving skip flag:', error);
      setEditedQuotes((prev) => (prev ? { ...prev, [skipField]: !checked } : prev));
    }
  };

  const renderSkipCheckbox = (skipField: SkipField) => (
    <div className="student-voice-skip-checkbox-container">
      <FormControlLabel
        control={
          <Checkbox
            checked={isSectionSkipped(skipField)}
            onChange={(e) => handleSkipSection(skipField, e.target.checked)}
          />
        }
        label="Skip this slide"
      />
    </div>
  );

  const renderSkippedMessage = () => (
    <div className="student-voice-skipped-message">
      <div className="student-voice-skipped-message-icon">
        <img src={WarningIcon} alt="warning" />
      </div>
      <div className="student-voice-skipped-message-text">
        <p className="student-voice-skipped-message-text-bold">This slide was intentionally left blank</p>
        <p className="student-voice-skipped-message-text-normal">It was marked as skipped</p>
      </div>
    </div>
  );

  const renderQuoteModeIndicator = (isSkipped: boolean) => (
    <div className="student-voice-quote-mode-indicator">
      {isSkipped ? (
        <span>&nbsp;</span>
      ) : (
        <>
          <span>Quote mode enabled</span>
          <CustomTooltip
            title="The quotation marks have been applied, you don't need to add them manually to your quote text."
            placement="top"
            arrow={true}
          >
            <IconButton className="info-icon">
              <img className="icon-size" src={InfoIcon} alt="Info" />
            </IconButton>
          </CustomTooltip>
        </>
      )}
    </div>
  );

  const renderSectionButtons = (showSurveyButton = false) => (
    <div className="student-voice-section-button-container">
      <CustomButton
        variant="secondary"
        buttonText="See focus group quotes"
        icon={<img src={NewWindowIcon} alt="open" className="icon-size" />}
        iconPosition="start"
        buttonAction={() => window.open(`/school-focus-group/${schoolId}`, '_blank', 'noopener,noreferrer')}
        className="student-voice-section-button"
      />
      {showSurveyButton && (
        <CustomButton
          variant="secondary"
          buttonText="See survey report"
          icon={<img src={NewWindowIcon} alt="open" className="icon-size" />}
          iconPosition="start"
          buttonAction={() => window.open('/survey-reports', '_blank', 'noopener,noreferrer')}
          className="student-voice-section-button"
        />
      )}
    </div>
  );

  const buildPatchOperations = (): JsonPatchOperation[] => {
    if (!editedQuotes) return [];

    const patchOperations: JsonPatchOperation[] = [];
    const allQuoteFields: QuoteTextField[] = [
      'student_voices_focus_group_summary_1',
      'student_voices_focus_group_summary_2',
      'student_voices_focus_group_summary_3',
      'student_voices_focus_group_summary_4',
      ...QUOTE_FIELDS,
    ];

    allQuoteFields.forEach((field) => {
      const currentValue = (quotes?.[field] as string | null) ?? null;
      const editedValue = (editedQuotes[field] as string | null) ?? null;
      if (currentValue !== editedValue) {
        let valueToSave = editedValue || '';
        if (QUOTE_FIELDS.includes(field)) {
          valueToSave = valueToSave.replace(/[“”]/g, '');
        }
        patchOperations.push({
          op: 'replace',
          path: `/${field}`,
          value: valueToSave,
        });
      }
    });

    return patchOperations;
  };

  const handleAutoSave = async () => {
    if (!userDetails || !schoolId || !editedQuotes) return;

    try {
      const patchOperations = buildPatchOperations();

      if (patchOperations.length > 0) {
        await patchSchoolReportQuotes(userDetails.eoaId, schoolId, userDetails.access_token, patchOperations);
        setQuotes(editedQuotes);
        setShowAutoSaveMessage(true);
      }
    } catch (error) {
      console.error('Error auto-saving quotes:', error);
    }
  };

  const handleSaveQuotes = async () => {
    if (!userDetails || !schoolId || !editedQuotes) return;

    setIsSaving(true);
    try {
      const patchOperations = buildPatchOperations();

      if (patchOperations.length > 0) {
        await patchSchoolReportQuotes(userDetails.eoaId, schoolId, userDetails.access_token, patchOperations);
        setQuotes(editedQuotes);
      }

      const transitionName =
        workflowStatus === 'QUOTE_SELECTION_NOT_STARTED' ? 'complete_quote_selection' : 'select_quotes';

      await executeQuoteSelectionWorkflowTransition(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token,
        transitionName
      );

      const statusResponse = await getQuoteSelectionWorkflowStatus(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token
      );
      setWorkflowStatus(statusResponse.current_state);
      setIsLockModalOpen(false);
    } catch (error) {
      console.error('Error saving quotes:', error);
      handleOpenErrorModal('Error occurred while saving quotes!', error as AxiosError);
    } finally {
      setIsSaving(false);
    }
  };

  const renderQuoteField = (field: QuoteTextField, label: string, isSkipped: boolean, wrapItem = false) => {
    if (!isEditable) {
      return (
        <div className="student-voice-summary-list-item">
          <h3 className="student-voice-summary-list-label">{label}</h3>
          <p
            className={`student-voice-summary-list-text ${!quotes?.[field] ? 'student-voice-placeholder-text' : '“”'}`}
          >
            {quotes?.[field] || 'No content provided'}
          </p>
        </div>
      );
    }

    const header = (
      <div className="student-voice-quote-item-header">
        <h3 className="student-voice-placeholder-title">{label}</h3>
        {renderQuoteModeIndicator(isSkipped)}
      </div>
    );
    const textarea = (
      <textarea
        className="student-voice-edit-textarea"
        value={getQuoteDisplayValue(field)}
        onChange={(e) => handleQuoteWithQuotes(field, e.target.value, e)}
        onFocus={(e) => handleQuoteFocus(field, e)}
        onBlur={handleAutoSave}
        onPaste={(e) => handleQuotePaste(field, e)}
        onDrop={(e) => handleQuoteDrop(field, e)}
        placeholder={isSkipped ? '' : '"Your quote here"'}
        disabled={isSkipped}
      />
    );

    if (wrapItem) {
      return (
        <div className="student-voice-quote-item">
          {header}
          {textarea}
        </div>
      );
    }

    return (
      <>
        {header}
        {textarea}
      </>
    );
  };

  const renderQuoteSection = (
    skipField: SkipField,
    fields: { field: QuoteTextField; label: string }[],
    wrapItem = false
  ) => (
    <>
      {isEditable && renderSkipCheckbox(skipField)}
      {!isEditable && isSavedSectionSkipped(skipField)
        ? renderSkippedMessage()
        : (() => {
            const isSkipped = isSectionSkipped(skipField);
            return fields.map(({ field, label }) => (
              <Fragment key={field}>{renderQuoteField(field, label, isSkipped, wrapItem)}</Fragment>
            ));
          })()}
    </>
  );

  if (loading || !userDetails) return <Loader />;

  return (
    <>
      <TranscriptReviewModal
        isOpen={isApproveModalOpen}
        handleClose={() => setIsApproveModalOpen(false)}
        handleConfirm={handleApprove}
        type="approve"
        isLoading={isProcessing}
        customMessage={
          <>
            By confirming, you are <b>approving</b> the student voice content for school report.
          </>
        }
      />
      <TranscriptReviewModal
        isOpen={isRejectModalOpen}
        handleClose={() => setIsRejectModalOpen(false)}
        handleConfirm={handleReject}
        type="reject"
        isLoading={isProcessing}
        customMessage={
          <>
            By confirming, you are <b>rejecting</b> the student voice content for school report.
          </>
        }
      />
      <TranscriptReviewModal
        isOpen={isLockModalOpen}
        handleClose={() => setIsLockModalOpen(false)}
        handleConfirm={handleSaveQuotes}
        type="approve"
        isLoading={isSaving || isProcessing}
        customMessage={
          <>
            By confirming, you'll finalize and lock the <b>Student Voice content</b> for the school report. Once locked,
            it can't be edited or changed.
          </>
        }
        confirmButtonText="Confirm"
        cancelButtonText="Keep editing"
      />
      <TranscriptReviewModal
        isOpen={isReopenModalOpen}
        handleClose={() => setIsReopenModalOpen(false)}
        handleConfirm={handleReopen}
        type="reject"
        isLoading={isProcessing}
        customMessage={
          <>
            By confirming, you are <b>reviewing the approved content again.</b>
          </>
        }
      />
      <div className="student-voice-review-container">
        <CustomButton
          variant="text"
          buttonText="Back"
          icon={<img src={GoBackIcon} alt="go back" />}
          iconPosition="start"
          buttonAction={() => navigate('/student-voice-for-reports')}
        />
        <div className="student-voice-review-header-wrapper">
          <div className="student-voice-review-header">
            <div className="student-voice-review-header-title">
              <h1 className="student-voice-review-school-name">{schoolName}</h1>
              {workflowStatus && <StudentVoiceStatus status={workflowStatus} />}
            </div>
          </div>
          <div className="student-voice-review-description">
            <p>
              Provide student quotes and themes that reflect what you heard in your assigned school's focus groups or
              surveys.
            </p>
            <p>
              These insights will be featured in the school report to highlight student voices. To see how your input
              will appear in context,{' '}
              <Link to="/school-reports" className="student-voice-review-description-link">
                view the full school report template
              </Link>
              .
            </p>
          </div>
          <div className="student-voice-review-header-actions">
            {isEditable ? (
              <CustomButton
                variant="primary"
                buttonText="Lock in content"
                buttonAction={() => setIsLockModalOpen(true)}
                disabled={isSaving || isProcessing}
              />
            ) : (
              <>
                {showReopenButton && (
                  <CustomButton
                    variant="secondary"
                    buttonText="Re-open content"
                    buttonAction={() => setIsReopenModalOpen(true)}
                    disabled={isProcessing}
                  />
                )}
                {canApproveOrReject && (
                  <>
                    <CustomButton
                      variant="secondary"
                      buttonText="Reject content"
                      buttonAction={() => setIsRejectModalOpen(true)}
                      disabled={isProcessing}
                    />
                    <CustomButton
                      variant="primary"
                      buttonText="Approve content"
                      buttonAction={() => setIsApproveModalOpen(true)}
                      disabled={isProcessing}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="student-voice-review-content">
          <div className="student-voice-section">
            <h2 className="student-voice-section-title">1. Student voices: Focus group summary</h2>
            <p className="student-voice-section-description">
              Add a summary of key insights from the focus group session. This summary will help capture the overall
              themes and takeaways from the student discussion.
            </p>
            {isEditable && renderSectionButtons()}
            <div
              className={
                isEditable ? 'student-voice-quote-section-layout-editable' : 'student-voice-quote-section-layout'
              }
            >
              <div className="student-voice-image-left">
                <img src="/images/student-voice-focus-group-summary.png" alt="Focus group summary" />
              </div>
              <div className="student-voice-quote-right">
                <div className="student-voice-summary-list-item">
                  <h3 className="student-voice-summary-list-label">Perception of school</h3>
                  {isEditable ? (
                    <>
                      <textarea
                        className="student-voice-edit-textarea"
                        value={editedQuotes?.student_voices_focus_group_summary_1 || ''}
                        onChange={(e) => handleQuoteChange('student_voices_focus_group_summary_1', e.target.value)}
                        onBlur={handleAutoSave}
                        onPaste={(e) => handleSummaryPaste('student_voices_focus_group_summary_1', e)}
                        onDrop={(e) => handleSummaryDrop('student_voices_focus_group_summary_1', e)}
                        placeholder="Your text here"
                      />
                    </>
                  ) : (
                    <p
                      className={`student-voice-summary-list-text ${!quotes?.student_voices_focus_group_summary_1 ? 'student-voice-placeholder-text' : '“”'}`}
                    >
                      {quotes?.student_voices_focus_group_summary_1 || 'No content provided'}
                    </p>
                  )}
                </div>
                <div className="student-voice-summary-list-item">
                  <h3 className="student-voice-summary-list-label">Perception of support systems</h3>
                  {isEditable ? (
                    <>
                      <textarea
                        className="student-voice-edit-textarea"
                        value={editedQuotes?.student_voices_focus_group_summary_2 || ''}
                        onChange={(e) => handleQuoteChange('student_voices_focus_group_summary_2', e.target.value)}
                        onBlur={handleAutoSave}
                        onPaste={(e) => handleSummaryPaste('student_voices_focus_group_summary_2', e)}
                        onDrop={(e) => handleSummaryDrop('student_voices_focus_group_summary_2', e)}
                        placeholder="Your text here"
                      />
                    </>
                  ) : (
                    <p
                      className={`student-voice-summary-list-text ${!quotes?.student_voices_focus_group_summary_2 ? 'student-voice-placeholder-text' : '“”'}`}
                    >
                      {quotes?.student_voices_focus_group_summary_2 || 'No content provided'}
                    </p>
                  )}
                </div>
                <div className="student-voice-summary-list-item">
                  <h3 className="student-voice-summary-list-label">Perception of classes and assignments</h3>
                  {isEditable ? (
                    <>
                      <textarea
                        className="student-voice-edit-textarea"
                        value={editedQuotes?.student_voices_focus_group_summary_3 || ''}
                        onChange={(e) => handleQuoteChange('student_voices_focus_group_summary_3', e.target.value)}
                        onBlur={handleAutoSave}
                        onPaste={(e) => handleSummaryPaste('student_voices_focus_group_summary_3', e)}
                        onDrop={(e) => handleSummaryDrop('student_voices_focus_group_summary_3', e)}
                        placeholder="Your text here"
                      />
                    </>
                  ) : (
                    <p
                      className={`student-voice-summary-list-text ${!quotes?.student_voices_focus_group_summary_3 ? 'student-voice-placeholder-text' : '“”'}`}
                    >
                      {quotes?.student_voices_focus_group_summary_3 || 'No content provided'}
                    </p>
                  )}
                </div>
                <div className="student-voice-summary-list-item">
                  <h3 className="student-voice-summary-list-label">Opportunities</h3>
                  {isEditable ? (
                    <>
                      <textarea
                        className="student-voice-edit-textarea"
                        value={editedQuotes?.student_voices_focus_group_summary_4 || ''}
                        onChange={(e) => handleQuoteChange('student_voices_focus_group_summary_4', e.target.value)}
                        onBlur={handleAutoSave}
                        onPaste={(e) => handleSummaryPaste('student_voices_focus_group_summary_4', e)}
                        onDrop={(e) => handleSummaryDrop('student_voices_focus_group_summary_4', e)}
                        placeholder="Your text here"
                      />
                    </>
                  ) : (
                    <p
                      className={`student-voice-summary-list-text ${!quotes?.student_voices_focus_group_summary_4 ? 'student-voice-placeholder-text' : '“”'}`}
                    >
                      {quotes?.student_voices_focus_group_summary_4 || 'No content provided'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="student-voice-section" data-testid="section-key-findings">
            <h2 className="student-voice-section-title">2. Student voices: Key findings</h2>
            <p className="student-voice-section-description">
              Add a student quote that highlights a key insight from the focus group session or survey report. This
              quote will help illustrate one of the main findings in the school report.
            </p>
            {isEditable && <div className="student-voice-section-header-actions">{renderSectionButtons(true)}</div>}
            <div
              className={
                isEditable ? 'student-voice-quote-section-layout-editable' : 'student-voice-quote-section-layout'
              }
            >
              <div className="student-voice-image-left">
                <img src="/images/student-voice-key-findings.png" alt="Key findings" />
              </div>
              <div className="student-voice-quote-right">
                {renderQuoteSection('key_findings_ending_student_voice_is_skip', [
                  { field: 'key_findings_ending_student_voice', label: 'Student quote' },
                ])}
              </div>
            </div>
          </div>

          <div className="student-voice-section" data-testid="section-college-eligibility">
            <h2 className="student-voice-section-title">3. Student voices: College eligibility</h2>
            <p className="student-voice-section-description">
              Add two student quotes that highlight key insights from the focus group session or survey. These quotes
              should help illustrate students' perspectives on college eligibility, and relate to the information
              presented in slides 35-39 with a focus on access (aka enrollment) and success (getting passing grades).
            </p>
            {isEditable && renderSectionButtons(true)}
            <div
              className={
                isEditable ? 'student-voice-quote-section-layout-editable' : 'student-voice-quote-section-layout'
              }
            >
              <div className="student-voice-image-left">
                <img src="/images/student-voice-college-eligibility.png" alt="College eligibility" />
              </div>
              <div className="student-voice-quote-right">
                {renderQuoteSection(
                  'college_eligibility_student_voices_1_is_skip',
                  [
                    { field: 'college_eligibility_student_voices_1_1', label: 'Student quote 1' },
                    { field: 'college_eligibility_student_voices_1_2', label: 'Student quote 2' },
                  ],
                  true
                )}
              </div>
            </div>
          </div>

          <div className="student-voice-section">
            <h2 className="student-voice-section-title">4. Student voices: College eligibility 2</h2>
            <p className="student-voice-section-description">
              Add two student quotes that highlight key insights from the focus group session or survey. These quotes
              should help illustrate students' perspectives on college eligibility, and relate to the information
              presented in slides 41-47 with a focus on students' struggles to succeed in the courses they need to be
              college eligible and/or issues related to staying on track.
            </p>
            {isEditable && renderSectionButtons(true)}
            <div
              className={
                isEditable ? 'student-voice-quote-section-layout-editable' : 'student-voice-quote-section-layout'
              }
            >
              <div className="student-voice-image-left">
                <img src="/images/student-voice-college-eligibility-2.png" alt="College eligibility 2" />
              </div>
              <div className="student-voice-quote-right">
                {renderQuoteSection(
                  'college_eligibility_student_voices_2_is_skip',
                  [
                    { field: 'college_eligibility_student_voices_2_1', label: 'Student quote 3' },
                    { field: 'college_eligibility_student_voices_2_2', label: 'Student quote 4' },
                  ],
                  true
                )}
              </div>
            </div>
          </div>

          <div className="student-voice-section">
            <h2 className="student-voice-section-title">
              5. Student voices: Postsecondary success via college readiness
            </h2>
            <p className="student-voice-section-description">
              Add two student quotes that highlight key insights from the focus group session or survey. These quotes
              should help illustrate students' perspectives on postsecondary success via college readiness.
            </p>
            {isEditable && <div className="student-voice-section-header-actions">{renderSectionButtons(true)}</div>}
            <div
              className={
                isEditable ? 'student-voice-quote-section-layout-editable' : 'student-voice-quote-section-layout'
              }
            >
              <div className="student-voice-image-left">
                <img src="/images/student-voice-college-readiness.png" alt="College readiness" />
              </div>
              <div className="student-voice-quote-right">
                {renderQuoteSection(
                  'college_readiness_student_voices_1_is_skip',
                  [
                    { field: 'college_readiness_student_voices_1_1', label: 'Student quote 1' },
                    { field: 'college_readiness_student_voices_1_2', label: 'Student quote 2' },
                  ],
                  true
                )}
              </div>
            </div>
          </div>

          <div className="student-voice-section">
            <h2 className="student-voice-section-title">
              6. Student voices: Postsecondary success via college readiness 2
            </h2>
            <p className="student-voice-section-description">
              Add two student quotes that highlight key insights from the focus group session or survey. These quotes
              should help illustrate students' perspectives on postsecondary success via college readiness, and relate
              to the data shared on slides 60-75.
            </p>
            {isEditable && <div className="student-voice-section-header-actions">{renderSectionButtons(true)}</div>}
            <div
              className={
                isEditable ? 'student-voice-quote-section-layout-editable' : 'student-voice-quote-section-layout'
              }
            >
              <div className="student-voice-image-left">
                <img src="/images/student-voice-college-readiness-2.png" alt="College readiness 2" />
              </div>
              <div className="student-voice-quote-right">
                {renderQuoteSection(
                  'college_readiness_student_voices_2_is_skip',
                  [
                    { field: 'college_readiness_student_voices_2_1', label: 'Student quote 3' },
                    { field: 'college_readiness_student_voices_2_2', label: 'Student quote 4' },
                  ],
                  true
                )}
              </div>
            </div>
          </div>

          <div className="student-voice-section">
            <h2 className="student-voice-section-title">
              7. Student voices: Postsecondary success via CTE pathway completion
            </h2>
            <p className="student-voice-section-description">
              Add two student quotes that highlight key insights from the focus group session or survey. These quotes
              should help illustrate students' perspectives on postsecondary success via CTE pathway completion.
            </p>
            {isEditable && <div className="student-voice-section-header-actions">{renderSectionButtons(true)}</div>}
            <div
              className={
                isEditable ? 'student-voice-quote-section-layout-editable' : 'student-voice-quote-section-layout'
              }
            >
              <div className="student-voice-image-left">
                <img src="/images/student-voice-cte-pathway.png" alt="CTE pathway completion" />
              </div>
              <div className="student-voice-quote-right">
                {renderQuoteSection(
                  'pss_via_cte_student_voices_is_skip',
                  [
                    { field: 'pss_via_cte_student_voices_1', label: 'Student quote 1' },
                    { field: 'pss_via_cte_student_voices_2', label: 'Student quote 2' },
                  ],
                  true
                )}
              </div>
            </div>
          </div>

          <div className="student-voice-section" data-testid="section-synthesis">
            <h2 className="student-voice-section-title">8. Student voices: Synthesis, sensemaking & next steps</h2>
            <p className="student-voice-section-description">
              Add a student quote that highlights a key insight from the focus group session or survey report. This
              quote will help school team members as they synthesize all the information from the report and make sense
              of the data they have been unpacking as a team.
            </p>
            {isEditable && <div className="student-voice-section-header-actions">{renderSectionButtons(true)}</div>}
            <div
              className={
                isEditable ? 'student-voice-quote-section-layout-editable' : 'student-voice-quote-section-layout'
              }
            >
              <div className="student-voice-image-left">
                <img src="/images/student-voice-synthesis.png" alt="Synthesis, sensemaking & next steps" />
              </div>
              <div className="student-voice-quote-right">
                {renderQuoteSection('next_steps_student_voices_is_skip', [
                  { field: 'next_steps_student_voices', label: 'Student quote' },
                ])}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={showAutoSaveMessage}
        autoHideDuration={4000}
        onClose={() => setShowAutoSaveMessage(false)}
        message="Draft saved automatically."
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        className="student-voice-auto-save-snackbar"
      />
    </>
  );
};

export default StudentVoiceReview;
